import {
  Breadcrumb,
  Button,
  ErrorAlert,
  Input,
  Modal,
  modalHandler,
  ModalOpenBtn,
  PhotoIcon,
  success,
  SwitchInput,
  TableRowsPerPage,
  UploadImageCard,
  useCustomForm,
  useState,
} from "@";
import PageHero from "@/shared/components/miscellaneous/PageHero";
import { useCanShow, useDelete } from "@/shared/hooks";
import { useCarouselPage } from "../hooks";
import { useSaveCarouselMutation } from "../api/mutations";
import { defaultValues, schema } from "../lib/validation";
import { CarouselGrid } from "../components/CarouselGrid";
import { cardZone } from "../../shared/cards";
import type { CarouselItem } from "../types";

const Page = () => {
  const links = [{ name: "البانر الرئيسي", link: "" }];
  const [isEdit, setIsEdit] = useState(false);
  const canEdit = useCanShow("editMainBanner");

  // Form methods
  const {
    watch,
    reset,
    errors,
    register,
    setValue,
    getValues,
    handleSubmit,
    isSubmitting,
    isSubmitSuccessful,
  } = useCustomForm(defaultValues, schema);

  // List page hook — React Query backed, server-paginated
  const { data, setData, isLoading, error, totalItems, pg, updatePg, refetch } =
    useCarouselPage();
  const { deleteHandler } = useDelete(data, setData, "sliders");
  const saveCarouselMutation = useSaveCarouselMutation();

  const clearForm = () => {
    setIsEdit(false);
    reset(defaultValues);
  };

  const submit = async () => {
    try {
      await saveCarouselMutation.mutateAsync(getValues());
      success("تم الحفظ بنجاح");
      modalHandler();
      clearForm();
    } catch (error) {}
  };

  const handlerEdit = (item: CarouselItem) => {
    reset(item);
    setIsEdit(true);
    modalHandler();
  };

  return (
    <>
      {/* Error alert */}
      <ErrorAlert reload={refetch} show={error} />
      {/* Breadcrumb navigation */}
      <Breadcrumb links={links} />

      {/* Modal for adding/editing carousel slides */}
      <Modal close={clearForm} title={isEdit ? "تعديل" : "اضافة"}>
        <form onSubmit={handleSubmit(submit)} className="p-2 md:p-5">
          {/* Banner name */}
          <Input
            style="mb-5"
            label="اسم البانر"
            error={errors.title}
            input={register("title")}
          />

          {/* Banner order */}
          <Input
            star
            style="mb-7"
            label="الترتيب"
            error={errors.order}
            input={register("order")}
          />

          {/* Banner image */}
          <UploadImageCard
            show={true}
            title="صورة البانر"
            src={getValues("imageUrl")}
            success={isSubmitSuccessful}
            error={errors.image?.message}
            upload={(file: File) => setValue("image", file)}
          />

          {/* Active status */}
          <SwitchInput
            label="نشط"
            isBordered={true}
            checked={watch("isActive")}
            change={(value: boolean) => setValue("isActive", value)}
          />

          {/* Submit */}
          <Button
            className="mt-5 w-full"
            isLoading={isSubmitting}
            type="submit"
            title="حفظ البيانات"
          />
        </form>
      </Modal>

      <main className="table-card relative">
        <PageHero
          eyebrow="الموقع / البانر"
          title="البانر الرئيسي"
          count={totalItems}
          icon={PhotoIcon}
          hideCount={isLoading}
          headerClassName="flex-row items-center justify-between"
        >
          <TableRowsPerPage
            pagination={{ ...pg, rows: totalItems }}
            onRowsPerPageChange={(size: number) => updatePg({ size, page: 1 })}
          />

          {canEdit && <ModalOpenBtn title="اضافة" />}
        </PageHero>

        {/* Banner cards */}
        <div className={cardZone}>
          <CarouselGrid
            data={data}
            isLoading={isLoading}
            totalItems={totalItems}
            pg={pg}
            updatePg={updatePg}
            onEdit={handlerEdit}
            deleteHandler={deleteHandler}
          />
        </div>
      </main>
    </>
  );
};

export default Page;
